import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { useTranslation } from '@infra/i18n';
import { brandColors, brandRgba, brandShadowRgba, TOUCH_TARGET_MIN_PX } from '@shared/constants';

const DANGER_C = brandColors.danger;
const TEXT_PRIMARY = brandColors.textPrimary;
const TEXT_TERTIARY = brandColors.textTertiary;
const WHITE = brandColors.surfaceCard;
const BORDER = brandColors.border;

/** Height assumed per row before the menu has been measured, for flip-up maths. */
const ESTIMATED_ROW_H = 44;
const MENU_EDGE_GAP = 12;

export interface RowActionItem {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** Stable list key; the index is used when absent. */
  key?: string;
}

/**
 * The "⋮" actions menu used on admin table rows.
 *
 * Implements the WAI-ARIA menu button pattern: the popup is a `menu` of
 * `menuitem`s that owns arrow-key navigation, so Tab does not have to walk
 * through the actions one by one — it closes the menu instead.
 *
 * The popup renders in a portal, because a table cell would clip it. Keys are
 * handled on the popup itself (focus is always inside it while open); only
 * outside-click has to be watched on the document, since the portal is not in
 * the trigger's subtree.
 */
export function RowActionsMenu({ items, label }: { items: RowActionItem[]; label?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const menuLabel = label ?? t('common:a11y.rowActions');

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuHeight = menuRef.current?.offsetHeight || items.length * ESTIMATED_ROW_H + 16;
    const spaceBelow = window.innerHeight - r.bottom;
    let top = r.bottom + 6;
    if (spaceBelow < menuHeight + MENU_EDGE_GAP && r.top > spaceBelow) {
      top = Math.max(MENU_EDGE_GAP, r.top - menuHeight - 6);
    } else if (top + menuHeight > window.innerHeight - MENU_EDGE_GAP) {
      top = Math.max(MENU_EDGE_GAP, window.innerHeight - menuHeight - MENU_EDGE_GAP);
    }
    setPos({ top, right: Math.max(MENU_EDGE_GAP, window.innerWidth - r.right) });
  }, [items.length]);

  // Clamped, not just stored: an action list that shrinks while the menu is
  // open (a row whose status flipped) would otherwise leave every item with
  // tabIndex -1, costing the menu its tab stop.
  const currentIndex = Math.min(activeIndex, items.length - 1);

  // The open effect seeds focus from this once and must NOT re-run when it
  // changes — re-running would re-register the listeners and fight the roving
  // focus it exists to start. Reading it through a ref states that in code
  // rather than hiding it from the dependency check. Declared before that
  // effect, so on the commit where the menu opens this has already published
  // the matching value.
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  });

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) btnRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    // Entry point only — ArrowUp on the trigger opens onto the last item.
    itemRefs.current[currentIndexRef.current]?.focus();

    const onPointerDown = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const reposition = () => place();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, place]);

  const focusItem = (index: number) => {
    const next = (index + items.length) % items.length;
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem(currentIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem(currentIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(items.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        // A menu is a single tab stop: leaving it closes it rather than
        // walking the actions. Focus returns to the trigger so the browser
        // continues the tab sequence from the row, not from the portal.
        close(true);
        break;
      default:
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!open) {
            place();
            setActiveIndex(0);
          }
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          // Opening with a key lands on an item, matching the menu button pattern.
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            place();
            setActiveIndex(e.key === 'ArrowDown' ? 0 : items.length - 1);
            setOpen(true);
          }
        }}
        style={{
          width: TOUCH_TARGET_MIN_PX,
          height: TOUCH_TARGET_MIN_PX,
          borderRadius: 8,
          display: 'grid',
          placeItems: 'center',
          border: 'none',
          background: 'transparent',
          color: TEXT_TERTIARY,
          cursor: 'pointer',
        }}
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical size={16} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            // Focusable so a click on the menu's own padding does not drop
            // focus to <body>, which would strand Escape and the arrow keys.
            tabIndex={-1}
            aria-label={menuLabel}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              zIndex: 4000,
              minWidth: 170,
              background: WHITE,
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: 6,
              boxShadow: `0 16px 40px ${brandShadowRgba('textPrimary', 0.16)}`,
            }}
          >
            {items.map((it, i) => (
              <button
                key={it.key ?? i}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                // Roving tabindex: the menu is one tab stop, arrows move within it.
                tabIndex={i === currentIndex ? 0 : -1}
                onFocus={() => setActiveIndex(i)}
                onClick={() => {
                  // Hand focus back before the item unmounts, so a dialog this
                  // action opens records the trigger as its opener and returns
                  // focus there on close.
                  close(true);
                  it.onClick();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  width: '100%',
                  minHeight: TOUCH_TARGET_MIN_PX,
                  padding: '8px 10px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  color: it.danger ? DANGER_C : TEXT_PRIMARY,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = it.danger
                    ? brandRgba('dangerWash', 0.08)
                    : brandColors.surfaceTint;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <it.icon size={15} />
                {it.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
